/**
 * Super-Admin — Funktions-Check: testet jede KI-Funktion live über die System-Keys.
 */
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { ListChecks, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { formatDateTime } from "@/lib/formatDate";

export default function AiFunctionCheckCard() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const r = await apiClient.get("/admin/ai-providers/function-check");
      setData(r.data?.id ? r.data : null);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try {
      const r = await apiClient.post("/admin/ai-providers/function-check");
      setData(r.data);
      if (r.data.ok_count === r.data.total) toast.success(`Alle ${r.data.total} KI-Funktionen laufen über eure Keys`);
      else toast.warning(`${r.data.ok_count}/${r.data.total} Funktionen OK – Details siehe Tabelle`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Funktions-Check fehlgeschlagen");
    } finally { setRunning(false); }
  };

  return (
    <Card className="p-5 border-slate-200" data-testid="ai-function-check-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-violet-700" />
            <span className="text-lg font-semibold text-slate-900">Alle KI-Funktionen testen</span>
            {data && (
              <Badge className={data.ok_count === data.total ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} data-testid="function-check-badge">
                {data.ok_count}/{data.total} OK
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Ruft jede Funktion (Belegerkennung, Kontierung, Assistent, Websuche, Whisper, OCR …) mit einer Mini-Probe auf und zeigt, welcher Provider, welches Modell und welcher Key verwendet wurde. Dauer ca. 30–60 s.
          </p>
        </div>
        <Button size="sm" onClick={run} disabled={running} data-testid="function-check-run-btn">
          {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ListChecks className="h-4 w-4 mr-1" />}
          {running ? "Läuft …" : "Jetzt testen"}
        </Button>
      </div>

      {data ? (
        <>
          <div className="text-xs text-slate-500 mb-2">Letzter Lauf {formatDateTime(data.created_at)}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="function-check-table">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="pb-1 pr-3 font-medium">Status</th><th className="pb-1 pr-3 font-medium">Funktion</th><th className="pb-1 pr-3 font-medium">Provider</th>
                  <th className="pb-1 pr-3 font-medium">Modell</th><th className="pb-1 pr-3 font-medium">Key</th><th className="pb-1 pr-3 font-medium">Zeit</th><th className="pb-1 font-medium">Antwort / Fehler</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr key={r.function} className="border-t border-slate-100" data-testid={`function-check-row-${r.function.split(" ")[0].toLowerCase()}`}>
                    <td className="py-1.5 pr-3">{r.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}</td>
                    <td className="py-1.5 pr-3 text-slate-800">{r.function}</td>
                    <td className="py-1.5 pr-3 text-slate-700">{r.provider || "–"}</td>
                    <td className="py-1.5 pr-3 font-mono text-slate-700">{r.model || "–"}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{r.key_source}</td>
                    <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">{r.ms} ms</td>
                    <td className={`py-1.5 ${r.ok ? "text-slate-600" : "text-rose-700"}`}>{r.ok ? (r.sample || "OK") : (r.error || "Fehler")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : <div className="text-sm text-slate-500 py-3" data-testid="function-check-empty">Noch kein Test ausgeführt.</div>}
    </Card>
  );
}
